import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { PARTNERS_PAGE_TABS } from '@/data/constants'
import { getLastRoutePath } from '@/utils/route-utils'
import MobileTabMenu from './mobile-tab-menu'
import { Button } from '@/components/ui/button'
import { Icons } from '@/theme/Images'
import AddPartnerDrawer from './add-partner-drawer'
import { usePartners } from '../hooks/use-partners'
import { processMidString } from '@/utils/mid-utils'

const PartnersPageLayout = () => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const selectedTab = getLastRoutePath(pathname)
  const [isOpen, setIsOpen] = useState(false)
  const [initialPartnerData, setInitialPartnerData] = useState(null)
  const [manualAddJsonInput, setManualAddJsonInput] = useState('')
  const { groups, refetch } = usePartners()

  useEffect(() => {
    if (selectedTab === 'partners') navigate('all-partners')
  }, [selectedTab, navigate])

  // Check for URL params to open drawer with pre-filled data
  useEffect(() => {
    const params = new URLSearchParams(search)
    const openDrawer = params.get('openDrawer')
    if (openDrawer === 'true') {
      const mids = params.get('mids') || ''
      const merchantName = params.get('merchantName') || ''

      // Process mids to filter by prefix and strip prefixes (even if empty)
      const processedData = processMidString(mids)
      // Include merchantName in the initial data
      setInitialPartnerData({
        ...processedData,
        merchantName: merchantName,
      })
      setIsOpen(true)
      // Clean up URL params
      navigate('/partners/all-partners', { replace: true })
    }
  }, [search, navigate])

  const handlePartnerAdded = () => {
    refetch()
    setIsOpen(false)
    setInitialPartnerData(null)
  }

  const handleDrawerClose = () => {
    setIsOpen(false)
    setInitialPartnerData(null)
  }

  return (
    <div className="md:px-[10%] md:pt-[2%] bg-bgGray min-h-screen h-auto p-5" data-testid="partners-page-container">
      <div className="flex flex-col md:gap-8 gap-5">
        <div className="flex justify-between items-center">
          <p className="text-2xl md:text-3xl/[38px] font-semibold text-[--gray-900]">Partners</p>
          <div>
            {selectedTab === 'all-partners' && (
              <Button
                className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white"
                onClick={() => setIsOpen(true)}
              >
                <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
                <span className="font-semibold text-sm ml-2">Add Partner</span>
              </Button>
            )}
          </div>
        </div>
        {/* Defines Partners tab*/}
        <div className="md:gap-2 md:border-b md:block relative">
          <div className="flex flex-row gap-3">
            <div className="md:flex hidden gap-3">
              {Object.entries(PARTNERS_PAGE_TABS).map(([key, tab]) => (
                <Link key={key} to={key}>
                  <div
                    className={cn(
                      selectedTab === key ? 'border-solid border-b-2 border-[--brand-600]' : null,
                      'px-1 pb-3'
                    )}
                  >
                    <div
                      className={`text-sm font-semibold px-0 ${
                        selectedTab === key ? 'text-brand-700' : 'text-dGray-500'
                      }`}
                    >
                      {tab.title}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="md:hidden pt-6 w-[327px]">
          <MobileTabMenu />
        </div>
        {/* Render the child routes */}
        <Outlet context={{ manualAddJsonInput, setManualAddJsonInput }} />
      </div>

      {/* Add Partner Drawer */}
      <AddPartnerDrawer
        isOpen={isOpen}
        onClose={handleDrawerClose}
        onPartnerAdded={handlePartnerAdded}
        groups={groups}
        initialPartnerData={initialPartnerData}
      />
    </div>
  )
}

export default PartnersPageLayout
